import type {
  HeaderAccountMenu,
  HeaderAccountState,
  HeaderLinkAction,
  HeaderRetryAction,
  HeaderSignIn,
  HeaderTransactionRow,
} from './model';

export function headerLink(
  href: HeaderLinkAction['href'],
  navigation: HeaderLinkAction['navigation'],
  { onSelect }: Pick<HeaderLinkAction, 'onSelect'> = {},
): HeaderLinkAction {
  return {
    kind: 'link',
    href,
    navigation,
    ...(onSelect ? { onSelect } : {}),
  };
}

type HeaderTransaction = {
  id: string;
  type: HeaderTransactionRow['icon'];
};

type HeaderTransactionRowsOptions<T extends HeaderTransaction> = {
  transactions: readonly T[];
  label: (transaction: T) => string;
  action: (transaction: T) => HeaderLinkAction;
};

export function headerTransactionRows<T extends HeaderTransaction>({
  transactions,
  label,
  action,
}: HeaderTransactionRowsOptions<T>): HeaderTransactionRow[] {
  return transactions.map((transaction) => ({
    key: `${transaction.type}-${transaction.id}`,
    label: label(transaction),
    icon: transaction.type,
    action: action(transaction),
  }));
}

type HeaderSessionUser = {
  customerUuid: string | null;
  email: string;
  fullName: string;
  firstName: string;
  isAgent?: boolean | null;
};

/**
 * The session fields the header reads. The apps' auth contexts match it structurally, so the
 * header takes no dependency on an auth client.
 */
type HeaderSession =
  | { state: 'authenticating' | 'not_authenticated' | 'error' }
  | { state: 'authenticated'; user: HeaderSessionUser };

type HeaderAccountStateOptions = {
  /** Null on routes that offer no sign-in. */
  signIn: HeaderSignIn | null;
  /** Null when another attempt cannot succeed. */
  retry: HeaderRetryAction | null;
  /** Called only for a signed-in customer. */
  menu: () => HeaderAccountMenu;
};

export function headerAccountState(
  session: HeaderSession,
  { signIn, retry, menu }: HeaderAccountStateOptions,
): HeaderAccountState {
  if (session.state !== 'authenticated') {
    if (session.state === 'authenticating') return { status: 'loading' };
    if (session.state === 'error') return { status: 'error', retry, signIn };
    return { status: 'signedOut', signIn };
  }
  const { user } = session;
  return {
    status: 'authenticated',
    identity: {
      key: user.customerUuid ?? user.email,
      fullName: user.fullName,
      firstName: user.firstName,
      email: user.email,
      badge: user.isAgent ? 'agent' : null,
    },
    menu: menu(),
  };
}
